import { customersApiClient } from './apiClient';
import {isDebug} from '@/utils/debug'

// Contract: api-docs/ratings-feedback-issues-api.md (plugin repo).
// POST /trips/{trip}/issues — description (required, ≤1000) + images[] (≤5,
// jpeg/png/jpg, ≤10 MB each) + video (one, mp4/mov/avi, ≤50 MB). Multipart when
// files are attached, plain JSON otherwise. The API has no category fields —
// the popup composes the selected categories/issues into the description text.
// Documents go as files[] (≤5) — not in the backend contract yet, so they're
// ignored server-side until it adds support; sending them is forward-compatible.
// GET  /trips/{trip}/issues — { issues: [...] (newest first), pagination }.

export const reportService = {
  async submitReport(tripId, { description, images = [], files = [], video = null } = {}) {
    // Debug: surface exactly what we send (files as name/size/type summaries).
    if (isDebug('hopnDebug')) console.log('[HOPN report] request', {
      endpoint: `POST /trips/${tripId}/issues`,
      multipart: images.length > 0 || files.length > 0 || !!video,
      descriptionLength: description?.length || 0,
      description,
      images: images.map((f) => ({ name: f.name, type: f.type, sizeMB: +(f.size / 1024 / 1024).toFixed(2) })),
      files: files.map((f) => ({ name: f.name, type: f.type, sizeMB: +(f.size / 1024 / 1024).toFixed(2) })),
      video: video ? { name: video.name, type: video.type, sizeMB: +(video.size / 1024 / 1024).toFixed(2) } : null,
    });

    let result;
    if (images.length > 0 || files.length > 0 || video) {
      const form = new FormData();
      form.append('description', description);
      images.forEach((file) => form.append('images[]', file));
      files.forEach((file) => form.append('files[]', file));
      if (video) form.append('video', video);
      result = await customersApiClient.postFormData(`/trips/${tripId}/issues`, form);
    } else {
      result = await customersApiClient.post(`/trips/${tripId}/issues`, { description });
    }

    if (isDebug('hopnDebug')) console.log('[HOPN report] response', result);

    if (!result.success) {
      return {
        success: false,
        issue: null,
        error: result.error || 'Failed to submit report',
        raw: result.raw,
      };
    }

    return { success: true, issue: result.data?.issue || null, error: null };
  },

  async getTripIssues(tripId) {
    const result = await customersApiClient.get(`/trips/${tripId}/issues`);

    if (!result.success) {
      return {
        success: false,
        issues: [],
        pagination: null,
        error: result.error || 'Failed to fetch reported issues',
      };
    }

    return {
      success: true,
      issues: result.data?.issues || [],
      pagination: result.data?.pagination || null,
      error: null,
    };
  },
};

export default reportService;
